import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Checkbox,
  Code,
  Group,
  Popover,
  Select,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconArrowUp, IconCode, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import useFetch from '../../helpers/useFetch';
import HelpPopover from '../HelpPopover/HelpPopover';
import SettingRow from '../SettingRow/SettingRow';
import { HELP } from './superAdmin/superAdminHelp';
import classes from './superAdmin/SuperAdminSections.module.css';
import {
  BUILT_IN_KEY,
  MAX_BOOST_VALUES,
  MAX_CARD_ROWS,
  MAX_CONTEXT_ROWS,
  MAX_LABEL_LENGTH,
  MAX_MAPPING_ROWS,
  MAX_TEMPLATE_LENGTH,
  ROLES,
  blankMappingRow,
  blankPriceDisplayRule,
  cardRowCount,
  contextRowCount,
  duplicateMappingKeys,
  hasBuiltInRow,
  isCustomTextRow,
  mappingRowErrors,
  mappingRowType,
  priceDisplayRuleErrors,
  reachesNoTemplate,
  toggleBuiltInRow,
} from '../../helpers/agentFieldMapping';

// Set on the domain, not the chat bot — shown here so the whole picture is in one place.
const DOMAIN_FIELDS = [
  { label: 'Brand', name: 'productsBrandFieldName' },
  { label: 'Colour', name: 'productsColorFieldName' },
  { label: 'Size', name: 'productsSizeFieldName' },
];

// The custom-field types the agent can read; anything else (date, …) is not mappable.
const FIELD_TYPES = [
  { type: 'string', group: 'Text fields' },
  { type: 'numeric', group: 'Number fields' },
];

const TEMPLATE_EXAMPLE =
  '{{#unless priceHidden}}{{formatPriceFlexible value currency locale 2 2}} / ' +
  "{{lookup (lookup (find custom.numeric 'key' 'unitPriceReferenceValue') 'values') 0}}" +
  "{{toLowerCase (lookup (lookup (find custom.string 'key' 'unitPriceReferenceUnit') 'values') 0)}}{{/unless}}";

// A key and its type in one Select value, because the same key can be both a text and a number field.
const fieldValue = (type, key) => `${type}:${key}`;
const splitFieldValue = (value) => {
  const separator = value.indexOf(':');
  return { type: value.slice(0, separator) === 'numeric' ? 'numeric' : 'string', key: value.slice(separator + 1) };
};

// Roles a number field cannot take, with the reason shown on the disabled checkbox.
const TEXT_ONLY_ROLES = {
  searchable: 'Number fields cannot be searched.',
  count: 'Number fields cannot be counted.',
};

// Mantine's default duplicate check ignores case; the API matches values exactly.
const isExactDuplicate = (value, current) => current.includes(value.trim());
const cleanTags = (values) => values.map((value) => value.trim()).filter(Boolean);

// An empty field is reported by Save, not while the row is still being filled in.
const shownError = (value, message) => (value === '' || value == null ? null : message);

/**
 * Super-admin editors for chatBot.agentFieldMapping and chatBot.priceDisplayRule. Controlled:
 * every change goes through `handleChange` with the whole new value, so ChatBotPage's dirty check
 * and Save see them like any other field. Field choices are the domain's product custom.string and
 * custom.numeric keys; a stored key that is no longer in the catalog stays selectable so the row
 * still reads.
 */
const AgentCatalogFields = ({ chatBot, handleChange, accountId, domainId, domain }) => {
  const fetch = useFetch();
  const [fields, setFields] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => {
        if (cancelled) return;
        const product = Array.isArray(customFields?.product) ? customFields.product : [];
        setFields(product.filter(({ type }) => FIELD_TYPES.some((mappable) => mappable.type === type)));
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, domainId, fetch]);

  const rows = Array.isArray(chatBot.agentFieldMapping) ? chatBot.agentFieldMapping : [];
  const rule = chatBot.priceDisplayRule ?? null;
  const emitRows = (value) => handleChange({ target: { name: 'agentFieldMapping', value } });
  const emitRule = (value) => handleChange({ target: { name: 'priceDisplayRule', value } });

  const keysOfType = (type) => fields.filter((field) => field.type === type).map((field) => field.key);
  // The price rule matches text values, so it keeps offering text fields only.
  const keyOptions = (current) => [...new Set([...keysOfType('string'), current].filter(Boolean))];
  const fieldData = (row) =>
    FIELD_TYPES.map(({ type, group }) => {
      const keys = [...new Set([...keysOfType(type), ...(row.key && mappingRowType(row) === type ? [row.key] : [])])];
      return { group, items: keys.map((key) => ({ value: fieldValue(type, key), label: key })) };
    }).filter(({ items }) => items.length > 0);

  const duplicates = duplicateMappingKeys(rows);
  const contextCount = contextRowCount(rows);
  const cardCount = cardRowCount(rows);

  const replaceRow = (index, next) => emitRows(rows.map((row, position) => (position === index ? next : row)));
  const updateRow = (index, changes) => replaceRow(index, { ...rows[index], ...changes });
  const changeKey = (index, value) => {
    // Boost values are exact values of the old field, so they go with it.
    const { boost: _previous, ...row } = rows[index];
    const { type, key } = splitFieldValue(value || '');
    // The label follows the field until it has been edited by hand.
    const label = !row.label || row.label === row.key ? key : row.label;
    // The API refuses a number field that is searchable or counted.
    const roles = row.roles.filter((role) => type !== 'numeric' || !TEXT_ONLY_ROLES[role]);
    replaceRow(index, { ...row, type, key, label, roles });
  };
  const toggleRole = (index, role, on) => {
    const current = rows[index].roles;
    // The API takes Count only with Context: ticking Count ticks Context, unticking Context unticks Count.
    const roles = ROLES.map(({ value }) => value).filter((value) => {
      if (value === role) return on;
      if (on && role === 'count' && value === 'context') return true;
      if (!on && role === 'context' && value === 'count') return false;
      return current.includes(value);
    });
    updateRow(index, { roles });
  };
  const changeBoost = (index, values) => {
    const { boost: _previous, ...row } = rows[index];
    const cleaned = cleanTags(values);
    replaceRow(index, cleaned.length ? { ...row, boost: cleaned } : row);
  };

  const settingsPath = `/accounts/${accountId}/domains/${domainId}/settings/domain?tab=productSync`;
  const ruleErrors = priceDisplayRuleErrors(rule);

  return (
    <>
      <SettingRow label="Brand, colour and size" help={<HelpPopover {...HELP.domainFields} />}>
        <Group gap="md" wrap="wrap">
          {DOMAIN_FIELDS.map(({ label, name }) => (
            <Group key={name} gap={4} wrap="nowrap">
              <Text size="xs" c="dimmed">
                {label}
              </Text>
              <Text size="xs" c={domain?.[name] ? undefined : 'dimmed'} data-testid={`${name}-value`}>
                {domain?.[name] || 'Not set'}
              </Text>
            </Group>
          ))}
          <Text size="xs" c="dimmed">
            Edit in{' '}
            <Anchor component={Link} to={settingsPath} size="xs">
              product sync settings
            </Anchor>
          </Text>
        </Group>
      </SettingRow>

      <SettingRow label="Field mapping" top wide help={<HelpPopover {...HELP.fieldMapping} />}>
        {loadError && (
          <Text size="xs" c="red" mb="xs">
            Could not load the domain&apos;s custom fields.
          </Text>
        )}
        <Box className={`${classes.fold} ${classes.fields}`} data-fold="fields">
          <Table verticalSpacing={2} fz="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Field</Table.Th>
                <Table.Th>Label</Table.Th>
                <Table.Th ta="center">Context</Table.Th>
                <Table.Th ta="center">Searchable</Table.Th>
                <Table.Th ta="center">Card</Table.Th>
                <Table.Th ta="center">Count</Table.Th>
                <Table.Th ta="center">Boost</Table.Th>
                <Table.Th ta="center">Template</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  {BUILT_IN_KEY}{' '}
                  <Text span size="xs" c="dimmed">
                    built in
                  </Text>
                </Table.Td>
                <Table.Td c="dimmed">{BUILT_IN_KEY}</Table.Td>
                <Table.Td />
                <Table.Td ta="center" data-label="Searchable">
                  <Checkbox
                    size="xs"
                    display="inline-block"
                    aria-label={`Searchable ${BUILT_IN_KEY}`}
                    checked={hasBuiltInRow(rows)}
                    disabled={!hasBuiltInRow(rows) && rows.length >= MAX_MAPPING_ROWS}
                    onChange={(e) => emitRows(toggleBuiltInRow(rows, e.currentTarget.checked))}
                  />
                </Table.Td>
                <Table.Td />
                <Table.Td />
                <Table.Td />
                <Table.Td />
                <Table.Td />
              </Table.Tr>

              {rows.map((row, index) => {
                if (row.source === 'builtIn') return null;
                // Numbered among the custom rows, as the Save error numbers them.
                const n = rows.slice(0, index).filter((other) => other.source !== 'builtIn').length + 1;
                const errors = mappingRowErrors(row, duplicates);
                const numeric = mappingRowType(row) === 'numeric';
                const template = (row.template || '').trim();
                const boost = Array.isArray(row.boost) ? row.boost : [];
                return (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" align="flex-start">
                        <Select
                          size="xs"
                          w={180}
                          aria-label={`Field ${n}`}
                          placeholder="Pick a field"
                          data={fieldData(row)}
                          value={row.key ? fieldValue(mappingRowType(row), row.key) : null}
                          searchable
                          allowDeselect={false}
                          error={shownError(row.key, errors.key)}
                          onChange={(value) => changeKey(index, value)}
                        />
                        {numeric && (
                          <Text span size="xs" c="dimmed" mt={6}>
                            number
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        w={160}
                        aria-label={`Label ${n}`}
                        placeholder={row.key || 'Label'}
                        maxLength={MAX_LABEL_LENGTH}
                        value={row.label || ''}
                        error={shownError(row.label, errors.label)}
                        onChange={(e) => updateRow(index, { label: e.currentTarget.value })}
                      />
                    </Table.Td>
                    {ROLES.map(({ value, label }) => {
                      const textOnly = numeric && TEXT_ONLY_ROLES[value];
                      const checkbox = (
                        <Checkbox
                          size="xs"
                          display="inline-block"
                          aria-label={`${label} ${n}`}
                          checked={row.roles?.includes(value) === true}
                          disabled={
                            Boolean(textOnly) ||
                            (value === 'card' && !row.roles?.includes('card') && cardCount >= MAX_CARD_ROWS)
                          }
                          error={value === 'context' ? errors.roles : null}
                          onChange={(e) => toggleRole(index, value, e.currentTarget.checked)}
                        />
                      );
                      return (
                        <Table.Td key={value} ta="center" data-label={label}>
                          {textOnly ? (
                            // A disabled checkbox fires no pointer events, so the tooltip hangs off a wrapper.
                            <Tooltip label={textOnly} withArrow>
                              <Box display="inline-block">{checkbox}</Box>
                            </Tooltip>
                          ) : (
                            checkbox
                          )}
                        </Table.Td>
                      );
                    })}
                    <Table.Td ta="center" data-label="Boost">
                      {(isCustomTextRow(row) || boost.length > 0) && (
                        <Popover width={320} position="bottom-end" withArrow shadow="md" trapFocus>
                          <Popover.Target>
                            <ActionIcon
                              variant={boost.length ? 'light' : 'subtle'}
                              color={errors.boost ? 'red' : boost.length ? 'blue' : 'gray'}
                              size="sm"
                              aria-label={`${boost.length ? 'Edit' : 'Add'} boost values ${n}`}
                            >
                              <IconArrowUp size={14} />
                            </ActionIcon>
                          </Popover.Target>
                          <Popover.Dropdown>
                            <TagsInput
                              size="xs"
                              aria-label={`Boost values ${n}`}
                              placeholder={`Up to ${MAX_BOOST_VALUES} values, comma separated`}
                              splitChars={[',']}
                              maxTags={MAX_BOOST_VALUES}
                              isDuplicate={isExactDuplicate}
                              value={boost}
                              error={errors.boost}
                              onChange={(values) => changeBoost(index, values)}
                            />
                          </Popover.Dropdown>
                        </Popover>
                      )}
                    </Table.Td>
                    <Table.Td ta="center" data-label="Template">
                      <Popover width={380} position="bottom-end" withArrow shadow="md" trapFocus>
                        <Popover.Target>
                          <ActionIcon
                            variant={template ? 'light' : 'subtle'}
                            color={errors.template ? 'red' : template ? 'blue' : 'gray'}
                            size="sm"
                            aria-label={`${template ? 'Edit' : 'Add'} template ${n}`}
                            // A template a row without Context or Card still carries stays editable, so it can be cleared.
                            disabled={!template && reachesNoTemplate(row)}
                          >
                            <IconCode size={14} />
                          </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown>
                          <Textarea
                            size="xs"
                            autosize
                            minRows={3}
                            maxRows={8}
                            aria-label={`Template ${n}`}
                            placeholder="Empty shows the raw values"
                            maxLength={MAX_TEMPLATE_LENGTH}
                            value={row.template || ''}
                            error={errors.template}
                            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                            onChange={(e) => updateRow(index, { template: e.currentTarget.value })}
                          />
                          <Text size="xs" c="dimmed" mt="xs">
                            Handlebars, rendered by the API over the product plus <Code fz="xs">value</Code> (the
                            row&apos;s values joined with &quot;, &quot;), <Code fz="xs">values</Code>,{' '}
                            <Code fz="xs">locale</Code>, <Code fz="xs">currency</Code> and{' '}
                            <Code fz="xs">priceHidden</Code>. Storefront helpers: <Code fz="xs">find</Code>,{' '}
                            <Code fz="xs">lookup</Code>, <Code fz="xs">formatPrice</Code>,{' '}
                            <Code fz="xs">formatPriceFlexible</Code>, <Code fz="xs">toLowerCase</Code>.
                          </Text>
                          <Text size="xs" c="dimmed" mt="xs">
                            Price per litre on a number <Code fz="xs">unitPrice</Code> field:
                          </Text>
                          <Code block fz={10} mt={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {TEMPLATE_EXAMPLE}
                          </Code>
                        </Popover.Dropdown>
                      </Popover>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        aria-label={`Remove field ${n}`}
                        onClick={() => emitRows(rows.filter((_, position) => position !== index))}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>

        {contextCount > MAX_CONTEXT_ROWS && (
          <Text size="xs" c="red" mt={4}>
            At most {MAX_CONTEXT_ROWS} Context fields — untick {contextCount - MAX_CONTEXT_ROWS} to save.
          </Text>
        )}

        <Group justify="space-between" mt={4}>
          <Group gap="md">
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconPlus size={12} />}
              disabled={rows.length >= MAX_MAPPING_ROWS}
              onClick={() => emitRows([...rows, blankMappingRow()])}
            >
              Add field
            </Button>
            {/* Here rather than in the header, which the folded table hides. */}
            {[HELP.count, HELP.boost].map((help) => (
              <Group key={help.label} gap={2} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  {help.label}
                </Text>
                <HelpPopover {...help} />
              </Group>
            ))}
          </Group>
          <Text size="xs" c="dimmed">
            {rows.length} of {MAX_MAPPING_ROWS}
          </Text>
        </Group>
      </SettingRow>

      <SettingRow label="Hide price when" htmlFor="priceDisplayRule" help={<HelpPopover {...HELP.priceDisplayRule} />}>
        <Switch
          id="priceDisplayRule"
          size="xs"
          checked={rule !== null}
          onChange={(e) => emitRule(e.currentTarget.checked ? blankPriceDisplayRule() : null)}
        />
        {rule && (
          <Group gap="xs" align="flex-start" mt="xs" wrap="nowrap">
            <Select
              size="xs"
              w={180}
              aria-label="Hide price field"
              placeholder="Pick a field"
              data={keyOptions(rule.hideWhen?.key)}
              value={rule.hideWhen?.key || null}
              searchable
              allowDeselect={false}
              error={shownError(rule.hideWhen?.key, ruleErrors.key)}
              onChange={(key) => emitRule({ hideWhen: { ...rule.hideWhen, key: key || '' } })}
            />
            <Text size="xs" mt={6} style={{ whiteSpace: 'nowrap' }}>
              is any of
            </Text>
            <TagsInput
              size="xs"
              style={{ flex: 1 }}
              aria-label="Hide price values"
              placeholder="Values, comma separated"
              splitChars={[',']}
              isDuplicate={isExactDuplicate}
              value={rule.hideWhen?.values || []}
              onChange={(values) => emitRule({ hideWhen: { ...rule.hideWhen, values: cleanTags(values) } })}
            />
          </Group>
        )}
      </SettingRow>
    </>
  );
};

export default AgentCatalogFields;
